/* VVB Globe night: interactive globe for sections/vvb-globe-night.liquid.
   Canvas 2D orthographic globe: land dots from Natural Earth polygons, great-circle routes from Seoul with comets, drag with inertia,
   pinch / keyboard, auto tour, three colour moods. Land and country data load from vvb-globe-night-data.json (see data-data-url).
   Each section instance is independent. The loop stops while the section is off screen, the tab is hidden or the section was re-rendered by the theme editor. */
(() => {
  if (window.__vgnLoaded) return;
  window.__vgnLoaded = true;

  const RAD = Math.PI / 180;
  const SEOUL = { lon: 126.978, lat: 37.5665 };
  const NSEG = 72;
  const MOODS = {
    midnight: { star: '255,255,255', land: [196, 184, 255], sphereA: '#1a1358', sphereB: '#080722', halo: '124,92,255', rim: '255,207,90', grat: '255,255,255', arcs: ['#ffcf5a', '#ff7ab8', '#8fa8ff', '#5ee6d0', '#c08bff', '#ff9a5a', '#7ad7ff'], light: false },
    sunset: { star: '255,230,220', land: [255, 214, 200], sphereA: '#4d1550', sphereB: '#14041f', halo: '255,95,143', rim: '255,179,107', grat: '255,225,215', arcs: ['#ffb36b', '#ff5f8f', '#ffd88a', '#c78bff', '#ff8a5c', '#ff9bd0', '#ffc27a'], light: false },
    pearl: { star: '108,61,240', land: [86, 66, 150], sphereA: '#ffffff', sphereB: '#dccff5', halo: '124,92,255', rim: '201,130,43', grat: '80,60,140', arcs: ['#6c3df0', '#c9822b', '#d9467a', '#2f7fd1', '#2aa58f', '#7a4fd6', '#e0903a'], light: true }
  };
  const instances = new WeakMap();
  let dataPromise = null;
  const loadData = url => dataPromise || (dataPromise = fetch(url).then(r => { if (!r.ok) throw new Error('globe data'); return r.json(); }));
  const vec = (lon, lat) => { const l = lon * RAD, p = lat * RAD, c = Math.cos(p); return [c * Math.sin(l), Math.sin(p), c * Math.cos(l)]; };
  const hex = (h, a) => { const n = parseInt(h.slice(1), 16); return `rgba(${n >> 16},${n >> 8 & 255},${n & 255},${a})`; };
  const wrapLon = a => ((a + 180) % 360 + 360) % 360 - 180;

  function create(root) {
    const cv = root.querySelector('[data-vgn-canvas]');
    const marketsEl = root.querySelector('[data-vgn-markets]');
    const ctx = cv && cv.getContext('2d');
    if (!cv || !ctx || !marketsEl) return null;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const narrowMq = matchMedia('(max-width: 860px)');
    const S = { tour: root.dataset.tour === 'true' && !reduce, tourSec: Math.max(3, +root.dataset.tourSeconds || 5), rotate: root.dataset.rotate === 'true' && !reduce, markets: [], sel: -1, hover: -1 };
    const view = { lon: 100, lat: 22, zoom: 1, tLon: null, tLat: null, tZoom: 1 };
    const vel = { x: 0, y: 0 };
    const seoulLabel = root.dataset.seoulLabel || 'SEOUL';
    const tourBtn = root.querySelector('[data-vgn-tour]');
    const pauseBtn = root.querySelector('[data-vgn-pause]');
    const countEl = root.querySelector('[data-vgn-count]');
    let D = null, land = [], stars = [], raw = [];
    let W = 0, H = 0, DPR = 1, cx = 0, cy = 0, R = 0;
    let T = 0, last = 0, lastUser = -1e9, lastTour = 0, dragging = false, paused = false, visible = false, raf = 0, counted = false, dead = false;
    let cl = 1, sl = 0, cp = 1, sp = 0;
    let sx = 0, sy = 0, moved = 0, pinch0 = 0, zoom0 = 1;
    const ptrs = new Map();
    try { raw = JSON.parse(marketsEl.textContent); } catch (e) { raw = []; }

    const setRot = () => { cl = Math.cos(view.lon * RAD); sl = Math.sin(view.lon * RAD); cp = Math.cos(view.lat * RAD); sp = Math.sin(view.lat * RAD); };
    const rot = w => { const x1 = w[0] * cl - w[2] * sl, z1 = w[0] * sl + w[2] * cl, y1 = w[1]; return [x1, y1 * cp - z1 * sp, y1 * sp + z1 * cp]; };
    const proj = r => { const k = R * view.zoom; return [cx + r[0] * k, cy - r[1] * k]; };
    const vis = r => !(r[2] < 0 && r[0] * r[0] + r[1] * r[1] < 1);

    function buildLand() {
      const w = 1440, h = 720, c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.fillStyle = '#fff';
      for (const ring of D.land) { g.beginPath(); ring.forEach((p, i) => { const x = (p[0] + 180) / 360 * w, y = (90 - p[1]) / 180 * h; i ? g.lineTo(x, y) : g.moveTo(x, y); }); g.closePath(); g.fill(); }
      const img = g.getImageData(0, 0, w, h).data, N = narrowMq.matches ? 16000 : 30000, ga = Math.PI * (3 - Math.sqrt(5));
      land = [];
      for (let i = 0; i < N; i++) {
        const phi = Math.asin(1 - 2 * (i + .5) / N), lam = ((ga * i + Math.PI) % (2 * Math.PI)) - Math.PI;
        const x = Math.min(w - 1, Math.floor((lam / Math.PI * 180 + 180) / 360 * w)), y = Math.min(h - 1, Math.floor((90 - phi / RAD) / 180 * h));
        if (img[(y * w + x) * 4 + 3] > 128) { const c2 = Math.cos(phi); land.push([c2 * Math.sin(lam), Math.sin(phi), c2 * Math.cos(lam)]); }
      }
    }
    function buildArc(m) {
      const a = vec(SEOUL.lon, SEOUL.lat), b = vec(m.lon, m.lat), d = Math.acos(Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]))), om = d || 1e-3, pts = [];
      for (let i = 0; i <= NSEG; i++) {
        const u = i / NSEG, s1 = Math.sin((1 - u) * om) / Math.sin(om), s2 = Math.sin(u * om) / Math.sin(om);
        const h = 0.012 + 0.34 * Math.pow(Math.sin(Math.PI * u), 1.15) * Math.pow(om / Math.PI, .72) + 0.05 * Math.sin(Math.PI * u) * (om / Math.PI);
        pts.push([(a[0] * s1 + b[0] * s2) * (1 + h), (a[1] * s1 + b[1] * s2) * (1 + h), (a[2] * s1 + b[2] * s2) * (1 + h)]);
      }
      m._arc = pts; m._w = b;
    }
    function resolveMarkets() {
      const byCode = {};
      D.countries.forEach(c => { byCode[c.code] = c; });
      S.markets = raw.map(r => {
        const c = byCode[r.code];
        const lon = c ? c.lon : parseFloat(r.lon), lat = c ? c.lat : parseFloat(r.lat);
        if (!isFinite(lon) || !isFinite(lat)) return null;
        return { name: r.name || (c ? c.name : 'Market'), lon, lat };
      }).filter(Boolean);
      S.markets.forEach(buildArc);
    }
    function resize() {
      const r = cv.parentElement.getBoundingClientRect();
      if (!r.width || !r.height) return;
      DPR = Math.min(narrowMq.matches ? 1.5 : 2, window.devicePixelRatio || 1);
      W = r.width; H = r.height;
      cv.width = Math.round(W * DPR); cv.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      const narrow = W < 860;
      cx = narrow ? W / 2 : W * .66; cy = H * .5; R = narrow ? Math.min(W * .4, H * .43) : Math.min(H * .4, W * .28);
    }
    function label(x, y, text, col, M, bold) {
      ctx.font = `${bold ? 800 : 700} 12px Manrope,system-ui,sans-serif`;
      const w = ctx.measureText(text).width + 22;
      ctx.fillStyle = M.light ? 'rgba(255,255,255,.92)' : 'rgba(10,8,30,.78)'; ctx.strokeStyle = col; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x - w / 2, y - 12, w, 24, 12) : ctx.rect(x - w / 2, y - 12, w, 24); ctx.fill(); ctx.stroke();
      ctx.fillStyle = M.light ? '#241a3d' : '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(text, x, y);
    }

    function fly(m) { view.tLon = m.lon; view.tLat = Math.max(-35, Math.min(55, m.lat * .75 + 8)); view.tZoom = 1.07; }
    function select(i, user) {
      if (i < 0 || i >= S.markets.length) { S.sel = -1; return; }
      S.sel = i; fly(S.markets[i]);
      if (user) { lastUser = performance.now(); lastTour = performance.now(); }
    }
    const next = () => { if (S.markets.length) select((S.sel + 1) % S.markets.length, false); };

    function frame(now) {
      raf = 0;
      if (dead) return;
      if (!root.isConnected) { destroy(); return; }
      if (!visible || document.hidden) return;
      const dt = Math.min(.05, (now - last) / 1000); last = now;
      if (!paused) T += dt;
      const t = T, M = MOODS[root.dataset.mood] || MOODS.midnight;
      if (view.tLon !== null) {
        const k = Math.min(1, dt * 3.2);
        view.lon += wrapLon(view.tLon - view.lon) * k; view.lat += (view.tLat - view.lat) * k; view.zoom += (view.tZoom - view.zoom) * k;
        if (Math.abs(wrapLon(view.tLon - view.lon)) < .15 && Math.abs(view.tLat - view.lat) < .15) view.tLon = null;
      } else if (S.rotate && !paused && !dragging && (now - lastUser > 2500) && !(S.tour && S.sel >= 0)) view.lon += dt * 4.5;
      if (!dragging && Math.abs(vel.x) + Math.abs(vel.y) > .001) { view.lon += vel.x; view.lat = Math.max(-70, Math.min(75, view.lat + vel.y)); vel.x *= .94; vel.y *= .94; }
      if (S.tour && !paused && !dragging && now - lastUser > 3500 && now - lastTour > S.tourSec * 1000) { next(); lastTour = now; }
      setRot();
      ctx.clearRect(0, 0, W, H);
      for (const s of stars) {
        const x = ((s.x * W - view.lon * s.z * 1.2) % W + W) % W, y = s.y * H + (view.lat * s.z * .6), tw = reduce ? .7 : .55 + .45 * Math.sin(t * 1.6 * s.s + s.p);
        ctx.fillStyle = `rgba(${M.star},${(M.light ? .35 : .9) * tw * Math.min(1, s.z)})`; ctx.fillRect(x, y, s.s * 1.2, s.s * 1.2);
      }
      const k = R * view.zoom;
      let g = ctx.createRadialGradient(cx, cy, k * .7, cx, cy, k * 1.9);
      g.addColorStop(0, `rgba(${M.halo},${M.light ? .28 : .5})`); g.addColorStop(.45, `rgba(${M.halo},${M.light ? .08 : .14})`); g.addColorStop(1, `rgba(${M.halo},0)`);
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      g = ctx.createRadialGradient(cx - k * .35, cy - k * .4, k * .1, cx, cy, k * 1.02); g.addColorStop(0, M.sphereA); g.addColorStop(1, M.sphereB);
      ctx.beginPath(); ctx.arc(cx, cy, k, 0, 6.2832); ctx.fillStyle = g; ctx.fill();
      ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, k, 0, 6.2832); ctx.clip();
      ctx.lineWidth = 1; ctx.strokeStyle = `rgba(${M.grat},${M.light ? .1 : .07})`;
      for (let lon = -180; lon < 180; lon += 30) { ctx.beginPath(); let on = false; for (let la = -85; la <= 85; la += 5) { const r = rot(vec(lon, la)); if (r[2] > 0) { const p = proj(r); on ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]); on = true; } else on = false; } ctx.stroke(); }
      for (let la = -60; la <= 60; la += 30) { ctx.beginPath(); let on = false; for (let lo = -180; lo <= 180; lo += 5) { const r = rot(vec(lo, la)); if (r[2] > 0) { const p = proj(r); on ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]); on = true; } else on = false; } ctx.stroke(); }
      const sz = Math.max(1.3, k * .0078), B = 8, buckets = Array.from({ length: B }, () => []);
      for (let i = 0; i < land.length; i++) { const r = rot(land[i]); if (r[2] > 0) { const p = proj(r), b = Math.min(B - 1, Math.floor(Math.pow(r[2], .55) * B)); buckets[b].push(p[0], p[1]); } }
      for (let b = 0; b < B; b++) { const a = (M.light ? .25 : .14) + (b / (B - 1)) * (M.light ? .7 : .8); ctx.fillStyle = `rgba(${M.land[0]},${M.land[1]},${M.land[2]},${a})`; ctx.beginPath(); const arr = buckets[b]; for (let i = 0; i < arr.length; i += 2) ctx.rect(arr[i] - sz / 2, arr[i + 1] - sz / 2, sz, sz); ctx.fill(); }
      g = ctx.createRadialGradient(cx, cy, k * .86, cx, cy, k * 1.07);
      g.addColorStop(0, `rgba(${M.halo},0)`); g.addColorStop(.72, `rgba(${M.halo},${M.light ? .16 : .3})`); g.addColorStop(.9, `rgba(${M.rim},${M.light ? .2 : .38})`); g.addColorStop(1, `rgba(${M.rim},0)`);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, k * 1.07, 0, 6.2832); ctx.fill();
      ctx.restore();
      ctx.beginPath(); ctx.arc(cx, cy, k, 0, 6.2832); ctx.lineWidth = 1.2; ctx.strokeStyle = `rgba(${M.rim},${M.light ? .35 : .45})`; ctx.stroke();
      ctx.globalCompositeOperation = M.light ? 'source-over' : 'lighter'; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      const n = S.markets.length, sel = S.sel, rs = rot(vec(SEOUL.lon, SEOUL.lat));
      for (let i = 0; i < n; i++) {
        const m = S.markets[i], col = M.arcs[i % M.arcs.length];
        const prog = reduce ? 1 : Math.max(0, Math.min(1, .3 + (t - i * .045) / 1.5)), pe = 1 - Math.pow(1 - prog, 3), maxI = Math.floor(pe * NSEG);
        const dim = sel >= 0 && sel !== i, base = dim ? .22 : .62, hot = sel === i;
        const pr = m._arc.slice(0, maxI + 1).map(rot);
        for (const pass of [0, 1]) {
          ctx.beginPath(); let on = false;
          for (let j = 0; j < pr.length; j++) { const r = pr[j]; if (vis(r)) { const p = proj(r); on ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]); on = true; } else on = false; }
          if (pass === 0) { ctx.lineWidth = hot ? 9 : 5; ctx.strokeStyle = hex(col, (hot ? .2 : .07) * (M.light ? 1.4 : 1)); } else { ctx.lineWidth = hot ? 2.6 : 1.15; ctx.strokeStyle = hex(col, hot ? .98 : base); }
          ctx.stroke();
        }
        if (!reduce && prog >= 1) {
          const per = (3.4 + (i % 5) * .55) * 2, u = ((t / per) + i * .37) % 1, hi = Math.floor(u * NSEG), tr = Math.floor(NSEG * .2);
          for (let j = Math.max(1, hi - tr); j <= hi; j++) {
            const a = rot(m._arc[j - 1]), b = rot(m._arc[j]);
            if (vis(a) && vis(b)) { const p = proj(a), q = proj(b), f = (j - (hi - tr)) / tr; ctx.beginPath(); ctx.moveTo(p[0], p[1]); ctx.lineTo(q[0], q[1]); ctx.lineWidth = 1 + f * (hot ? 3.4 : 2.4); ctx.strokeStyle = hex(col, f * f * (dim ? .5 : 1)); ctx.stroke(); }
          }
          const h = rot(m._arc[hi]);
          if (vis(h)) { const p = proj(h), rg = ctx.createRadialGradient(p[0], p[1], 0, p[0], p[1], 10); rg.addColorStop(0, hex('#ffffff', .95)); rg.addColorStop(.3, hex(col, .7)); rg.addColorStop(1, hex(col, 0)); ctx.fillStyle = rg; ctx.fillRect(p[0] - 10, p[1] - 10, 20, 20); }
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      for (let i = 0; i < n; i++) {
        const m = S.markets[i], r = rot(m._w);
        if (r[2] <= 0.02) continue;
        const p = proj(r), col = M.arcs[i % M.arcs.length], hot = sel === i || S.hover === i;
        const prog = reduce ? 1 : Math.max(0, Math.min(1, .3 + (t - i * .045) / 1.5));
        if (prog < .98) continue;
        const pulse = reduce ? .5 : ((t * .35 + i * .21) % 1);
        ctx.beginPath(); ctx.arc(p[0], p[1], (hot ? 7 : 3.2) + pulse * (hot ? 22 : 11), 0, 6.2832); ctx.strokeStyle = hex(col, (1 - pulse) * (hot ? .7 : .38)); ctx.lineWidth = 1.4; ctx.stroke();
        ctx.beginPath(); ctx.arc(p[0], p[1], hot ? 5.4 : 3.1, 0, 6.2832); ctx.fillStyle = M.light ? col : '#fff'; ctx.fill();
        ctx.beginPath(); ctx.arc(p[0], p[1], hot ? 9 : 5.4, 0, 6.2832); ctx.strokeStyle = hex(col, .9); ctx.lineWidth = 1.6; ctx.stroke();
        if (hot) label(p[0], p[1] - 18, m.name, col, M);
      }
      if (rs[2] > 0) {
        const p = proj(rs);
        for (let q = 0; q < 3; q++) { const f = reduce ? .4 : ((t * .55 + q / 3) % 1); ctx.beginPath(); ctx.arc(p[0], p[1], 6 + f * 34, 0, 6.2832); ctx.strokeStyle = `rgba(${M.rim},${(1 - f) * .7})`; ctx.lineWidth = 1.8; ctx.stroke(); }
        ctx.beginPath(); ctx.arc(p[0], p[1], 6.5, 0, 6.2832); ctx.fillStyle = `rgb(${M.rim})`; ctx.fill(); ctx.beginPath(); ctx.arc(p[0], p[1], 10.5, 0, 6.2832); ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
        label(p[0], p[1] - 22, seoulLabel, `rgb(${M.rim})`, M, true);
      }
      raf = requestAnimationFrame(frame);
    }
    function play() { if (dead || raf || !D || !visible || document.hidden) return; last = performance.now(); raf = requestAnimationFrame(frame); }
    function stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
    function countUp() {
      if (counted || !countEl || root.dataset.countAuto !== 'true') return;
      counted = true;
      const n = S.markets.length;
      if (reduce) { countEl.textContent = n; return; }
      const t1 = performance.now();
      (function f(now) { const k = Math.min(1, (now - t1) / 1200), e = 1 - Math.pow(1 - k, 3); countEl.textContent = Math.round(n * e); if (k < 1 && !dead) requestAnimationFrame(f); })(t1);
    }
    function destroy() { dead = true; stop(); io && io.disconnect(); ro && ro.disconnect(); document.removeEventListener('visibilitychange', onVis); }
    const onVis = () => { document.hidden ? stop() : play(); };

    /* interaction */
    const pick = (px, py) => { let best = -1, bd = 18 * 18; S.markets.forEach((m, i) => { const r = rot(m._w); if (r[2] <= 0) return; const p = proj(r), d = (p[0] - px) ** 2 + (p[1] - py) ** 2; if (d < bd) { bd = d; best = i; } }); return best; };
    cv.addEventListener('pointerdown', e => {
      cv.setPointerCapture(e.pointerId); ptrs.set(e.pointerId, [e.clientX, e.clientY]); dragging = true; cv.classList.add('is-drag');
      sx = e.clientX; sy = e.clientY; moved = 0; vel.x = vel.y = 0; view.tLon = null; lastUser = performance.now();
      if (ptrs.size === 2) { const a = [...ptrs.values()]; pinch0 = Math.hypot(a[0][0] - a[1][0], a[0][1] - a[1][1]); zoom0 = view.zoom; }
    });
    cv.addEventListener('pointermove', e => {
      const rect = cv.getBoundingClientRect(), px = e.clientX - rect.left, py = e.clientY - rect.top;
      if (!ptrs.has(e.pointerId)) { if (e.pointerType === 'mouse') { const h = pick(px, py); if (h !== S.hover) { S.hover = h; cv.classList.toggle('is-hit', h >= 0); } } return; }
      ptrs.set(e.pointerId, [e.clientX, e.clientY]); lastUser = performance.now();
      if (ptrs.size === 2) { const a = [...ptrs.values()], d = Math.hypot(a[0][0] - a[1][0], a[0][1] - a[1][1]); view.zoom = Math.max(.8, Math.min(1.8, zoom0 * d / pinch0)); return; }
      const dx = e.clientX - sx, dy = e.clientY - sy; sx = e.clientX; sy = e.clientY; moved += Math.abs(dx) + Math.abs(dy);
      const f = 57.3 / (R * view.zoom); view.lon -= dx * f; view.lat = Math.max(-70, Math.min(75, view.lat + dy * f)); vel.x = -dx * f * .6; vel.y = dy * f * .6;
    });
    const end = e => {
      ptrs.delete(e.pointerId);
      if (!ptrs.size) {
        dragging = false; cv.classList.remove('is-drag');
        if (moved < 6 && e.type === 'pointerup') { const rect = cv.getBoundingClientRect(), h = pick(e.clientX - rect.left, e.clientY - rect.top); if (h >= 0) select(h, true); }
      }
    };
    cv.addEventListener('pointerup', end); cv.addEventListener('pointercancel', end);
    cv.addEventListener('pointerleave', () => { S.hover = -1; cv.classList.remove('is-hit'); });
    cv.addEventListener('wheel', e => { if (Math.abs(e.deltaY) > 0 && (e.ctrlKey || e.shiftKey)) { e.preventDefault(); view.zoom = Math.max(.8, Math.min(1.8, view.zoom * (1 - e.deltaY * .0015))); lastUser = performance.now(); } }, { passive: false });
    cv.addEventListener('keydown', e => {
      const s = 6 / view.zoom; let u = true;
      if (e.key === 'ArrowLeft') view.lon -= s; else if (e.key === 'ArrowRight') view.lon += s; else if (e.key === 'ArrowUp') view.lat = Math.min(75, view.lat + s); else if (e.key === 'ArrowDown') view.lat = Math.max(-70, view.lat - s);
      else if (e.key === '+' || e.key === '=') view.zoom = Math.min(1.8, view.zoom + .1); else if (e.key === '-') view.zoom = Math.max(.8, view.zoom - .1); else u = false;
      if (u) { e.preventDefault(); lastUser = performance.now(); view.tLon = null; }
    });
    const zin = root.querySelector('[data-vgn-zoom-in]'), zout = root.querySelector('[data-vgn-zoom-out]');
    if (zin) zin.addEventListener('click', () => { view.zoom = Math.min(1.8, view.zoom + .15); view.tZoom = view.zoom; lastUser = performance.now(); });
    if (zout) zout.addEventListener('click', () => { view.zoom = Math.max(.8, view.zoom - .15); view.tZoom = view.zoom; lastUser = performance.now(); });
    const setTour = v => { S.tour = v; if (tourBtn) tourBtn.setAttribute('aria-pressed', String(v)); if (v) lastTour = performance.now() - S.tourSec * 1000 + 800; };
    if (tourBtn) { tourBtn.setAttribute('aria-pressed', String(S.tour)); tourBtn.addEventListener('click', () => setTour(!S.tour)); }
    const setPaused = v => { paused = v; root.classList.toggle('is-paused', v); if (pauseBtn) { pauseBtn.setAttribute('aria-pressed', String(v)); const l = pauseBtn.querySelector('[data-vgn-pause-label]'); if (l) l.textContent = v ? (pauseBtn.dataset.labelPlay || 'Play motion') : (pauseBtn.dataset.labelPause || 'Pause motion'); } };
    if (pauseBtn) pauseBtn.addEventListener('click', () => setPaused(!paused));
    if (reduce && pauseBtn) pauseBtn.hidden = true;

    /* visibility, size, start */
    const io = 'IntersectionObserver' in window ? new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible) { countUp(); play(); } else stop(); }, { threshold: 0.05 }) : null;
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => { resize(); }) : null;
    document.addEventListener('visibilitychange', onVis);
    loadData(root.dataset.dataUrl).then(d => {
      if (dead) return;
      D = d; buildLand(); resolveMarkets();
      stars = Array.from({ length: 240 }, () => ({ x: Math.random(), y: Math.random(), z: .3 + Math.random() * 1.1, p: Math.random() * 6.28, s: Math.random() * 1.4 + .3 }));
      resize(); ro ? ro.observe(cv.parentElement) : addEventListener('resize', resize);
      lastTour = performance.now();
      if (io) io.observe(root); else { visible = true; countUp(); play(); }
    }).catch(() => { root.classList.add('is-static'); });
    return { destroy };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-vgn]').forEach(root => { if (!instances.has(root)) instances.set(root, create(root)); });
  }
  initAll();
  document.addEventListener('shopify:section:load', e => initAll(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-vgn]').forEach(root => { const i = instances.get(root); if (i) i.destroy(); }));
})();
